import { useState } from "react";
import { object } from "yup";
import * as yup from "yup";
import { getFields } from "~services/utils/defaultValidations";

const usePriceSearchForm = ({ prazoPersonalizado = false }) => {
  const [values, setValues] = useState({ name: "", value: "" });

  const optionSelect = {
    incoterms: [
      { label: "CIF", value: 2 },
      { label: "FOB", value: 1 },
    ],
    prazo: [
      { label: "15 Dias", value: 15 },
      { label: "30 Dias", value: 30 },
      { label: "60 Dias", value: 60 },
      { label: "90 Dias", value: 90 },
    ],
  };

  let defaultValidation = {
    empresaId: yup.number().required("O campo empresa é obrigatório"),
    produtoId: yup.number().required("O campo produto é obrigatório"),
    embalagemId: yup.number().required("O campo embalagem é obrigatório"),
    incoterms: yup.number().required("O campo modal (incoterm) é obrigatório"),
    canalDistribuicaoId: yup.number().required("O campo canal de distribuição é obrigatório"),
    frete: yup.number().transform((value, originalValue) => {
      if (typeof originalValue === "string") {
        return parseFloat(originalValue.replace(/[a-zA-Z\s$.]/g, "").replace(",", "."));
      }
      return value;
    }),
    volume: yup
    .number()
    .nullable()
    .when(["volumeNoInfo", "volumeNoConsumption"], {
      is: (volumeNoInfo, volumeNoConsumption) => !volumeNoInfo && !volumeNoConsumption,
      then: (schema) =>
        schema
          .required("O volume é obrigatório.")
          .test(
            "checkPositive",
            "O Volume deve ser maior que zero.",
            (value) => value > 0
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
    unidadeMedidaId: yup.number().required("O campo unidade de medida é obrigatório"),
    grupoMaterialId: yup.number().required("O campo grupo de produtos é obrigatório"),
    prazoId: prazoPersonalizado ? yup.number().nullable() : yup.number().required("O campo de prazo é obrigatório"),
    outrosPrazos: yup.string().when("prazoId", (prazoId) => {
    if (prazoId === null) {
      return yup
        .string()
        .matches(/^[0-9/]*$/, "O campo Outros Prazos deve conter apenas números e o caractere '/'")
        .required("O campo Outros Prazos é obrigatório");
    }
    return yup.string().nullable();
  }),

  };

  const validationSchemaINDTemplate = {
    ...defaultValidation,
    filialId: yup.number(),
    precoFinalCif: yup
      .string()
      .test({
        name: "cifGreaterThanZero",
        message:
          "O valor de Preço CIF deve ser maior do que zero, caso não queria especificar um valor, assinale a opção abaixo",
        test: function (value) {
          const { noInfoPrecoCif } = this.parent;

          if (noInfoPrecoCif) return true;

          if (value) {
            const precoCifNumber = parseFloat(value.replace(/[a-zA-Z\s$.]/g, "").replace(",", "."));

            return precoCifNumber && !isNaN(precoCifNumber) && precoCifNumber > 0;
          }

          return false;
        },
      })
      .test({
        name: "fobOrCifFilled",
        message: "É necessário preencher pelo menos um dos valores de CIF ou FOB",
        test: function (value) {
          return true;
        },
      }),
    precoFinalFob: yup
      .string()
      .test({
        name: "fobGreaterThanZero",
        message:
          "O valor de Preço FOB deve ser maior do que zero, caso não queria especificar um valor, assinale a opção abaixo",
        test: function (value) {
          const { noInfoPrecoFob } = this.parent;

          if (noInfoPrecoFob) return true;

          if (value) {
            const precoFobNumber = parseFloat(value.replace(/[a-zA-Z\s$.]/g, "").replace(",", "."));

            return precoFobNumber && !isNaN(precoFobNumber) && precoFobNumber > 0;
          }

          return false;
        },
      })
      .test({
        name: "fobNotGreaterThanCif",
        message: "O valor de Preço FOB não pode ser maior do que o valor de Preço CIF",
        test: function (value) {
          const { precoFinalCif, noInfoPrecoCif } = this.parent;
          if (noInfoPrecoCif) return true;
          if (value && precoFinalCif) {
            const precoFobNumber = parseFloat(value.replace(/[a-zA-Z\s$.]/g, "").replace(",", "."));
            const precoCifNumber = parseFloat(precoFinalCif.replace(/[a-zA-Z\s$.]/g, "").replace(",", "."));

            return precoFobNumber <= precoCifNumber;
          }

          return true;
        },
      }),
  };

  const validationSchemaTACTemplate = {
    ...defaultValidation,
    sellOut: yup
      .string()
      .required("O valor do Sell Out é obrigatório")
      .test({
        name: "SellOutGraterThanZero",
        message: "O valor de Sell Out deve ser maior do que zero",
        test: function (value) {
          const sellOutValue = value ? parseFloat(value.replace(/[a-zA-Z\s$.]/g, "").replace(",", ".")) : 0;

          return sellOutValue && !isNaN(sellOutValue) && sellOutValue > 0;
        },
      }),
    sellIn: yup
      .string()
      .test({
        name: "SellInGreaterThanZero",
        message:
          "O valor de Sell In deve ser maior do que zero, caso não queria especificar um valor, assinale a opção abaixo",
        test: function (value) {
          const { noInfoSellIn } = this.parent;

          if (noInfoSellIn) return true;

          if (value) {
            const sellInValue = value ? parseFloat(value.replace(/[a-zA-Z\s$.]/g, "").replace(",", ".")) : 0;

            return sellInValue && !isNaN(sellInValue) && sellInValue > 0;
          }

          return false;
        },
      })
      .test("sellInGreaterThanSellOut", "Valor de Sell In deve ser menor que Sell Out", function (value) {
        const sellOutValue = this.parent.sellOut
          ? parseFloat(this.parent.sellOut.replace(/[a-zA-Z\s$.]/g, "").replace(",", "."))
          : NaN;
        const sellInValue = value ? parseFloat(value.replace(/[a-zA-Z\s$.]/g, "").replace(",", ".")) : NaN;

        if (isNaN(sellOutValue) || sellOutValue <= 0) {
          return this.createError({ message: "Favor preencher o preço Sell-Out" });
        }
        if (isNaN(sellInValue)) {
          return false;
        }
        return sellInValue < sellOutValue;
      }),
  };

  const validationSchemaIND = object({ ...validationSchemaINDTemplate });
  const validationSchemaTAC = object({ ...validationSchemaTACTemplate });

  const startFields = {
    empresaId: "",
    produtoId: "",
    grupoMaterialId: "",
    embalagemId: "",
    frete: 0,
    filialId: "",
    volume: 0,
    unidadeMedidaId: "",
    canalDistribuicaoId: "",
    prazoId: "",
    outrosPrazos: "",
    sellIn: "",
    incoterms: "",
    origem: "",
    precoFinalFob: "",
    precoFinalCif: "",
  };

  const [valuesInd, _setValuesInd] = useState(startFields);

  const [valuesTac, _setValuesTac] = useState({
    ...startFields,
    sellOut: "",
  });

  const validationSchemaEditTAC = object({
    ...getFields(validationSchemaTACTemplate, [
      "volume",
      "unidadeMedidaId",
      "prazoId",
      "canalDistribuicaoId",
      "sellIn",
      "sellOut",
      "outrosPrazos",
      "empresaId",
      "embalagemId",
      "produtoId",
      "grupoMaterialId",
    ]),
  });

  const validationSchemaEditIND = object({
    ...getFields(validationSchemaINDTemplate, [
      "volume",
      "unidadeMedidaId",
      "prazoId",
      "canalDistribuicaoId",
      "precoFinalCif",
      "precoFinalFob",
      "outrosPrazos",
      "empresaId",
      "embalagemId",
      "produtoId",
      "grupoMaterialId",
    ]),
  });

  const cpfCnpj = (newValue) => {
    const numbersOnly = newValue.replace(/[^\d]/g, "");
    if (numbersOnly.length <= 11) {
      // CPF
      return [/\d/, /\d/, /\d/, ".", /\d/, /\d/, /\d/, ".", /\d/, /\d/, /\d/, "-", /\d/, /\d/];
    } else {
      // CNPJ
      return [/\d/, /\d/, ".", /\d/, /\d/, /\d/, ".", /\d/, /\d/, /\d/, "/", /\d/, /\d/, /\d/, /\d/, "-", /\d/, /\d/];
    }
  };

  const editForm = () => {
    const selects = document.querySelectorAll(".search-wrapper.searchWrapper");

    function removeAtivoClassFromAll() {
      selects.forEach((item) => {
        item.classList.remove("ativo");
      });
    }

    selects.forEach((item, index) => {
      item.addEventListener("click", (event) => {
        event.stopPropagation();
        removeAtivoClassFromAll();
        item.classList.add("ativo");
      });
    });

    document.addEventListener("click", () => {
      removeAtivoClassFromAll();
    });
  };

  return {
    validationSchemaIND,
    validationSchemaTAC,
    validationSchemaEditIND,
    validationSchemaEditTAC,
    cpfCnpj,
    setValues,
    values,
    optionSelect,
    editForm,
    valuesInd,
    valuesTac,
  };
};

export default usePriceSearchForm;
